import React, { useState } from "react";
import { Box, Grid, Typography, Link, TextField } from "@mui/material";
import useAutocomplete from "@mui/material/useAutocomplete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { styled, useTheme } from "@mui/material/styles";

export type OptionType = {
  label: string;
  value: string | number;
};

interface FilterSelectOptionsProps {
  label: string;
  value: string;
  options: OptionType[];
  onChange: (value: string) => void;
  placeholder?: string;
}

const SelectBox = styled("div")<{ theme?: any }>(({ theme }) => ({
  position: "relative",
  width: "100%",
  borderRadius: 4,
  border: "1px solid #bdbdbd",
  backgroundColor: theme.palette.mode === "dark" ? "#fff" : "#fff",
  color: theme.palette.mode === "dark" ? "#a5a3ae" : "#a5a3ae",
  fontSize: 11,
  display: "flex",
  alignItems: "center",
  padding: "5px 0px 2px 0px",
  cursor: "pointer",
  "&:focus-within, &:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

const StyledInput = styled("input")({
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 11,
  width: "100%",
  padding: "7px 14px 7px 14px",
  color: "inherit",
  cursor: "pointer",
  "&::placeholder": {
    color: "#a5a3ae",
    opacity: 1,
  },
});

const DropdownIcon = styled(ArrowDropDownIcon)({
  position: "absolute",
  right: 8,
  pointerEvents: "none",
  color: "#888",
  fontSize: 28,
});

const Listbox = styled("ul")<{ theme?: any }>(({ theme }) => ({
  width: "100%",
  margin: 0,
  padding: 0,
  zIndex: 2,
  position: "absolute" as const,
  left: 0,
  top: "100%",
  listStyle: "none",
  backgroundColor: theme.palette.background.paper,
  overflow: "auto",
  maxHeight: 200,
  border: "1px solid rgba(0,0,0,.25)",
  borderRadius: 4,
  boxShadow: theme.shadows[2],
  "& li": {
    padding: "8px 12px",
    cursor: "pointer",
    backgroundColor: theme.palette.mode === "dark" ? "#fff" : "#fff",
    color: theme.palette.mode === "dark" ? "#a5a3ae" : "#a5a3ae",
    fontSize: 11,
  },
  "& li.Mui-focused": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  "& li[aria-selected='true']": {
    backgroundColor: theme.palette.action.selected,
  },
}));

export const FilterSelectOptions: React.FC<FilterSelectOptionsProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder,
}) => {
  const theme = useTheme();
  const [manual, setManual] = useState(false);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    popupOpen,
  } = useAutocomplete<OptionType, false, false, false>({
    id: `custom-filter-select-${label.replace(/\s/g, "-").toLowerCase()}`,
    options,
    value: options.find((opt) => String(opt.value) === value) || null,
    onChange: (_, newValue) => onChange(newValue ? String(newValue.value) : ""),
    getOptionLabel: (option) => option.label,
    isOptionEqualToValue: (opt, val) => opt.value === val.value,
  });

  return (
    <Box>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <label className="text-lable" htmlFor="custom-select">
            {label}
          </label>
        </Grid>
        <Grid item>
          <Link
            component="button"
            variant="caption"
            underline="hover"
            type="button"
            sx={{ fontSize: 11, color: "#a5a3ae", fontWeight: "500" }}
            onClick={() => setManual((prev) => !prev)}
          >
            {manual ? "Not In List" : "Not In List"}
          </Link>
        </Grid>
      </Grid>
      <Box>
        {!manual ? (
          <div style={{ position: "relative" }}>
            <SelectBox
              {...getRootProps()}
              theme={theme}
              tabIndex={0}
              aria-haspopup="listbox"
              aria-expanded={popupOpen}
            >
              <StyledInput {...getInputProps()} placeholder={placeholder} />
              <DropdownIcon />
            </SelectBox>
            {popupOpen && groupedOptions.length > 0 ? (
              <Listbox {...getListboxProps()} theme={theme}>
                {(groupedOptions as OptionType[]).map((option, index) => {
                  const { ...optionProps } = getOptionProps({
                    option,
                    index,
                  });
                  return (
                    <li {...optionProps}>
                      {option.label}
                    </li>
                  );
                })}
              </Listbox>
            ) : null}
          </div>
        ) : (
          <TextField
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            size="small"
            fullWidth
          />
        )}
      </Box>
    </Box>
  );
};
